import type { ReactNode } from 'react';

/**
 * Renders the small Markdown subset models use in replies: paragraphs,
 * headings, bullet and numbered lists, tables, **bold**, *italic* and
 * `code`. Everything becomes React elements, so text from the model can
 * never inject HTML.
 */
export function Markdown({ text }: { text: string }) {
  return <div className="md">{blocks(text)}</div>;
}

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*)/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const s = m[0];
    if (s.startsWith('**')) out.push(<strong key={m.index}>{s.slice(2, -2)}</strong>);
    else if (s.startsWith('`')) out.push(<code key={m.index}>{s.slice(1, -1)}</code>);
    else out.push(<em key={m.index}>{s.slice(1, -1)}</em>);
    last = m.index + s.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const cells = (row: string) =>
  row
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());

function blocks(text: string): ReactNode[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      out.push(<p key={i} className="md-h">{inline(heading[2]!)}</p>);
      i++;
      continue;
    }
    // Table: a header row, a |---| separator, then body rows.
    if (line.includes('|') && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1] ?? '')) {
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i]!.includes('|') && lines[i]!.trim()) rows.push(cells(lines[i++]!));
      out.push(
        <div key={i} className="md-table">
          <table>
            <thead>
              <tr>{head.map((c, j) => <th key={j}>{inline(c)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, k) => (
                <tr key={k}>{r.map((c, j) => <td key={j}>{inline(c)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    const bullet = /^\s*[-*•]\s+/;
    const numbered = /^\s*\d+[.)]\s+/;
    if (bullet.test(line) || numbered.test(line)) {
      const re = bullet.test(line) ? bullet : numbered;
      const items: string[] = [];
      while (i < lines.length && re.test(lines[i]!)) items.push(lines[i++]!.replace(re, ''));
      const list = items.map((it, k) => <li key={k}>{inline(it)}</li>);
      out.push(re === bullet ? <ul key={i}>{list}</ul> : <ol key={i}>{list}</ol>);
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^#{1,4}\s/.test(lines[i]!) && !bullet.test(lines[i]!) && !numbered.test(lines[i]!)) {
      para.push(lines[i++]!);
    }
    out.push(
      <p key={i}>
        {para.flatMap((p, k) => (k ? [<br key={`b${k}`} />, ...inline(p)] : inline(p)))}
      </p>,
    );
  }
  return out;
}
